// "Room empty → all OFF" — a SMART AUTOMATION room (2026-10-07): while its door
// is CLOSED, no motion for GraceSec seconds (default 30) = EMPTY → every switch/
// fan output that is on goes off, except the keep-on list — even with someone
// sitting still inside (the owner's choice). A door open = nothing is switched.
// The server checks once a minute, so the off lands 0–60 s after the deadline.
// WaitMin is no longer used.
//
// Server: Home Automation api/new/vacancy.js (vacancy_migration.sql). Shown as a
// section of AutomationsDialog. The sensors and buttons lists come from the same
// GetAutomations reply (`ctx`), so the two never disagree about what exists.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, Alert, TextField, MenuItem, Switch, IconButton, Checkbox, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useApp } from '../../core/state/AppContext';
import { getVacancyWatches, saveVacancyWatch, deleteVacancyWatch } from '../../core/api/api';
import { rfType, rfEventChoices } from '../../core/devices/rfTypes';

const DOOR_TYPES = ['door', 'safe', 'shutter'];
const ids = (csv) => String(csv || '').split(',').map(Number).filter(Boolean);
const keepList = (csv) => String(csv || '').split(',').map((s) => s.trim()).filter(Boolean);
const hm = (t) => (t ? String(t).replace('T', ' ').slice(11, 19) : '');   // seconds: the deadline is ~30 s

function blank(locationId) {
  return {
    WatchId: 0, LocationID: Number(locationId), Name: '', Enabled: 1, RoomID: 0,
    DoorRfDeviceId: 0, CloseMeaning: 'Close', MotionRfDeviceIds: '', GraceSec: 30, WaitMin: 5, KeepOn: '',
  };
}

function describe(w, ctx) {
  const door = ctx.sensors.find((s) => Number(s.RfDeviceId) === Number(w.DoorRfDeviceId));
  const motion = ids(w.MotionRfDeviceIds).map((id) => ctx.sensors.find((s) => Number(s.RfDeviceId) === id)).filter(Boolean);
  const keep = keepList(w.KeepOn).length;
  return `${door ? `🚪 ${door.Name}` : 'Door'} ${w.CloseMeaning || 'Close'} + no motion from ${motion.map((m) => m.Name).join(' / ') || '—'}`
    + ` for ${w.GraceSec} s → ${w.RoomName || 'room'} all OFF${keep ? ` (keeps ${keep} on)` : ''}`;
}

function Editor({ value, ctx, rooms, onCancel, onSaved }) {
  const { userId } = useApp();
  const [w, setW] = useState(value);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setW((x) => ({ ...x, [k]: v }));
  const doors = ctx.sensors.filter((s) => DOOR_TYPES.includes(rfType(s.RfType).key));
  const motions = ctx.sensors.filter((s) => rfType(s.RfType).key === 'motion');
  const door = ctx.sensors.find((s) => Number(s.RfDeviceId) === Number(w.DoorRfDeviceId));
  const closeChoices = door ? ((door.Events && door.Events.length) ? door.Events : rfEventChoices(door.RfType)) : ['Close'];
  // Only real switch / fan outputs — not gesture sensors or other inputs.
  const roomButtons = ctx.targets.filter((t) => Number(t.RoomID) === Number(w.RoomID)
    && (Number(t.On_off) > 0 || Number(t.Dimming) > 0));
  const chosenMotion = ids(w.MotionRfDeviceIds);
  const keep = keepList(w.KeepOn);

  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await saveVacancyWatch(userId, w);
      if (r?.data?.ok === false) throw new Error(r.data.error);
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || (e?.response?.status === 404
        ? 'The server does not have this yet — deploy the backend and run vacancy_migration.sql.'
        : `Could not save (${e.message}).`));
    } finally {
      setBusy(false);
    }
  };

  // What still blocks Save, in words — the button says it rather than just greying out.
  const missing = (!Number(w.RoomID) && 'Choose the room')
    || (!Number(w.DoorRfDeviceId) && 'Choose the door sensor')
    || (!chosenMotion.length && 'Choose a motion sensor')
    || '';
  const SEL = { '& .MuiSelect-select': { py: '6px' } };

  // Two rows of dropdowns — the whole watch fits on one screen (asked 2026-10-06).
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
        <TextField select size="small" label="Room to switch off" value={Number(w.RoomID) || ''} sx={{ width: 170, ...SEL }}
          onChange={(e) => setW((x) => ({ ...x, RoomID: Number(e.target.value), KeepOn: '' }))}>
          {rooms.map((r) => <MenuItem key={r.RoomID} value={Number(r.RoomID)}>{r.RoomName}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Door sensor" value={Number(w.DoorRfDeviceId) || ''} sx={{ width: 170, ...SEL }}
          onChange={(e) => set('DoorRfDeviceId', Number(e.target.value))}
          helperText={doors.length ? '' : 'No door sensor here'}>
          {doors.map((s) => <MenuItem key={s.RfDeviceId} value={Number(s.RfDeviceId)}>{`${rfType(s.RfType).icon} ${s.Name}`}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Reports" value={w.CloseMeaning} sx={{ width: 100, ...SEL }}
          onChange={(e) => set('CloseMeaning', e.target.value)}>
          {closeChoices.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Motion sensors" sx={{ width: 190, ...SEL }}
          SelectProps={{
            multiple: true,
            renderValue: (sel) => sel.map((id) => (motions.find((m) => Number(m.RfDeviceId) === Number(id)) || {}).Name || id).join(', '),
          }}
          value={chosenMotion}
          onChange={(e) => set('MotionRfDeviceIds', [].concat(e.target.value).map(Number).join(','))}
          helperText={motions.length ? '' : 'No RF motion sensor here'}>
          {motions.map((s) => (
            <MenuItem key={s.RfDeviceId} value={Number(s.RfDeviceId)} dense>
              <Checkbox size="small" checked={chosenMotion.includes(Number(s.RfDeviceId))} sx={{ p: 0.25, mr: 0.5 }} />
              {`👣 ${s.Name}`}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <TextField size="small" type="number" label="Off after no motion (s)" value={w.GraceSec} sx={{ width: 160 }}
          inputProps={{ min: 10, max: 3600 }} helperText="door closed, even if someone sits still"
          onChange={(e) => set('GraceSec', Math.max(10, Number(e.target.value) || 10))} />
        <TextField select size="small" label="Keep these ON" sx={{ flex: '1 1 260px', ...SEL }}
          disabled={!Number(w.RoomID)}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: (sel) => (sel.length ? `${sel.length} kept on` : 'Nothing — switch everything off'),
          }}
          InputLabelProps={{ shrink: true }}
          value={keep}
          onChange={(e) => set('KeepOn', [].concat(e.target.value).join(','))}
          helperText={Number(w.RoomID) ? `${roomButtons.length} switch/fan output${roomButtons.length === 1 ? '' : 's'} in this room` : 'choose the room first'}>
          {roomButtons.map((t) => {
            const k = `${t.UniqueNumber}/${t.Output}`;
            return (
              <MenuItem key={k} value={k} dense>
                <Checkbox size="small" checked={keep.includes(k)} sx={{ p: 0.25, mr: 0.5 }} />
                {`${Number(t.Dimming) > 0 && !(Number(t.On_off) > 0) ? '🌀 ' : ''}${t.OutputName || `#${t.Output}`} — ${t.DeviceName || t.UniqueNumber}`}
              </MenuItem>
            );
          })}
        </TextField>
      </Box>

      {/* ⚠️ PINNED: the dialog scrolls, and a Save below the fold read as
          "Save does not work". It stays in view, and says what is missing. */}
      <Box sx={{
        position: 'sticky', bottom: -16, zIndex: 2, bgcolor: 'background.paper',
        display: 'flex', alignItems: 'center', gap: 1, py: 1, borderTop: 1, borderColor: 'divider',
      }}
      >
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ flex: 1, minWidth: 0 }}>
          {error || missing}
        </Typography>
        <Button onClick={onCancel} disabled={busy}>Back</Button>
        <Button variant="contained" onClick={save} disabled={busy || !!missing}>Save</Button>
      </Box>
    </Box>
  );
}

export default function VacancyPanel({ locationId, ctx, onEditingChange }) {
  const { userId } = useApp();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditingState] = useState(null);
  // The parent hides its own list while a watch is edited — the editor gets the screen.
  const setEditing = (v) => { setEditingState(v); if (onEditingChange) onEditingChange(!!v); };

  const load = useCallback(() => {
    getVacancyWatches(locationId, userId)
      .then((r) => { if (r?.data?.ok === false) throw new Error(r.data.error); setData(r.data); setError(''); })
      .catch((e) => setError(e?.response?.status === 404
        ? 'Not on the server yet — deploy the backend and run vacancy_migration.sql.'
        : `Could not load (${e.message}).`));
  }, [locationId, userId]);
  useEffect(() => { if (locationId) load(); }, [locationId, load]);

  const rooms = (data && data.rooms) || [];
  return (
    <Box sx={{ mt: 1 }}>
      <Divider sx={{ mb: 1 }}><Typography variant="caption" color="text.secondary">Room empty → all OFF</Typography></Divider>
      {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
      {data && editing && (
        <Editor value={editing} ctx={ctx} rooms={rooms} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {data && !editing && (
        <>
          {!data.watches.length && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              When a room&apos;s door closes and nobody moves for a few minutes, switch the room off.
            </Typography>
          )}
          {data.watches.map((w) => (
            <Box key={w.WatchId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Switch size="small" checked={!!Number(w.Enabled)}
                onChange={async (e) => { await saveVacancyWatch(userId, { ...w, Enabled: e.target.checked ? 1 : 0 }).catch(() => {}); load(); }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2">{describe(w, ctx)}</Typography>
                {/* Motion-sensor health: a door opening that the sensor did not
                    see is a miss; enough misses → a warning. It never pauses the
                    switch-off (vacancy.js). */}
                <Typography
                  variant="caption"
                  sx={{ display: 'block' }}
                  color={Number(w.MotionMisses) > 0 ? 'warning.main' : 'text.secondary'}
                >
                  {Number(w.MotionMisses) > 0
                    ? `⚠ Motion sensor missed ${w.MotionMisses} door opening${Number(w.MotionMisses) === 1 ? '' : 's'}`
                      + `${Number(w.MotionMisses) >= (Number(w.MissLimit) || 3) ? ' — check it' : ''}`
                      + `${w.LastMotionAt ? ` · last motion ${hm(w.LastMotionAt)}` : ' · never detected'}`
                    : `👣 last motion ${w.LastMotionAt ? hm(w.LastMotionAt) : '—'}`}
                </Typography>
                {/* The room right now — what the server last concluded. */}
                <Typography
                  variant="caption"
                  sx={{ display: 'block', fontWeight: 700 }}
                  // eslint-disable-next-line no-nested-ternary
                  color={w.PresenceState == null ? 'text.secondary' : (Number(w.PresenceState) === 1 ? 'warning.main' : 'success.main')}
                >
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {`${w.PresenceState == null ? 'Room: unknown' : (Number(w.PresenceState) === 1 ? 'Room: OCCUPIED' : 'Room: UNOCCUPIED')}`
                    + `${w.PresenceChangedAt ? ` since ${hm(w.PresenceChangedAt)}` : ''}`
                    + `${w.DoorState ? ` · door ${String(w.DoorState).toLowerCase()}` : ''}`}
                </Typography>
                {w.WatchingUntil && (
                  <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>
                    {`last motion ${hm(w.WatchingSince)} — EMPTY at ${hm(w.WatchingUntil)} unless motion`}
                  </Typography>
                )}
                {w.LastResult && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {`${hm(w.LastResultAt)} — ${w.LastResult}`}
                  </Typography>
                )}
              </Box>
              <IconButton size="small" onClick={() => setEditing({ ...w })}><EditIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={async () => {
                // eslint-disable-next-line no-alert
                if (!window.confirm('Delete this room watch?')) return;
                await deleteVacancyWatch(w.WatchId, userId).catch(() => {});
                load();
              }}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => setEditing(blank(locationId))}>Watch a room</Button>
        </>
      )}
    </Box>
  );
}
